// Single Source of Truth for currency conversion and CEIL pricing across SuiteScanner

export const SUPPORTED_CURRENCIES = ['USD', 'INR', 'GBP', 'EUR'] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export type CurrencyMeta = {
    code: SupportedCurrency;
    symbol: string;
    label: string;
    name: string;
};

export const CURRENCY_METAS: Record<SupportedCurrency, CurrencyMeta> = {
    USD: { code: 'USD', symbol: '$', label: 'US$', name: 'US dollar' },
    INR: { code: 'INR', symbol: '₹', label: '₹', name: 'Indian rupee' },
    GBP: { code: 'GBP', symbol: '£', label: '£', name: 'British pound' },
    EUR: { code: 'EUR', symbol: '€', label: '€', name: 'Euro' }
};

// Conservative baseline fallback rates (1 INR = X target currency)
// Used when offline or until fresh rates are synchronized from the backend.
export const FALLBACK_RATES: Record<string, number> = {
    INR: 1,
    USD: 0.0116,
    EUR: 0.0108,
    GBP: 0.0091,
    AED: 0.0426,
    SGD: 0.0153,
    AUD: 0.0178,
    CAD: 0.0162
};

/**
 * Converts an amount from one currency to another using the business-wide CEIL rule.
 * Base currency for conversion is INR (where business payouts settle).
 *
 * Example:
 * convertPriceCeil(100, 'USD', 'INR', rates) -> Converts $100 to INR, ceil to nearest whole rupee.
 * convertPriceCeil(5000, 'INR', 'USD', rates) -> Converts ₹5000 to USD, ceil to nearest whole dollar.
 */
export function convertPriceCeil(
    amount: number | string,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number> = FALLBACK_RATES
): number {
    const num = Number(amount);
    if (isNaN(num) || num <= 0) return 0;

    const from = fromCurrency.toUpperCase();
    const to = toCurrency.toUpperCase();

    if (from === to) {
        return Math.ceil(num);
    }

    // Convert from origin currency to base INR
    const fromRate = rates[from] ?? FALLBACK_RATES[from] ?? 1;
    const inrAmount = from === 'INR' ? num : num / fromRate;

    // Convert from base INR to target currency
    const toRate = rates[to] ?? FALLBACK_RATES[to] ?? 1;
    const targetAmount = to === 'INR' ? inrAmount : inrAmount * toRate;

    // As required: CEIL value instead of decimals like 149.5USD
    return Math.ceil(targetAmount);
}

/**
 * Formats a clean ceiling-rounded price with its proper currency symbol.
 */
export function formatCurrency(amount: number | string, currencyCode: string): string {
    const num = Math.ceil(Number(amount) || 0);
    const code = currencyCode.toUpperCase() as SupportedCurrency;
    const meta = CURRENCY_METAS[code];

    if (meta) {
        if (code === 'USD') return `US$${num.toLocaleString()}`;
        if (code === 'INR') return `₹${num.toLocaleString()}`;
        return `${meta.symbol}${num.toLocaleString()}`;
    }

    return `${currencyCode} ${num.toLocaleString()}`;
}

/**
 * Formats a supplier-provided amount exactly as given: no currency conversion
 * and no ceiling, always two decimals (e.g. a 124.60 city tax stays 124.60).
 * Use for property-payable taxes/fees, which must match what ETG reports.
 */
export function formatExactAmount(amount: number | string, currencyCode: string): string {
    const num = Number(amount);
    const safe = Number.isFinite(num) ? num : 0;
    const text = safe.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const code = (currencyCode || 'USD').toUpperCase();
    const meta = CURRENCY_METAS[code as SupportedCurrency];

    if (code === 'USD') return `US$${text}`;
    if (meta) return `${meta.symbol}${text}`;
    return `${code} ${text}`;
}

export const EUROZONE_COUNTRIES = new Set([
    'ad', 'at', 'be', 'cy', 'ee', 'es', 'fi', 'fr', 'de', 'gr',
    'ie', 'it', 'lt', 'lu', 'lv', 'mc', 'me', 'mt', 'nl', 'pt',
    'si', 'sk', 'sm', 'va', 'xk'
]);

/**
 * Detects the user's 2-letter ISO country code (lowercase) dynamically from the browser environment:
 * 1. Timezone inference (e.g. Asia/Kolkata -> in, Europe/London -> gb, America/... -> us)
 *    Timezone is the primary indicator of the user's actual physical location.
 * 2. navigator.languages / navigator.language fallback (via Intl.Locale or language tag regex)
 * 3. Default fallback: 'in'
 */
export function detectUserCountry(): string {
    if (typeof window === 'undefined' && typeof navigator === 'undefined') {
        return 'in';
    }

    try {
        // 1. Timezone detection (primary indicator of physical location)
        if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz) {
                const tzLower = tz.toLowerCase();
                // India
                if (tzLower.includes('calcutta') || tzLower.includes('kolkata') || tzLower.includes('india')) return 'in';
                // UK
                if (tzLower.includes('london') || tzLower.includes('belfast')) return 'gb';
                // Americas / USA
                if (tzLower.startsWith('america/')) return 'us';
                // Europe
                if (tzLower.startsWith('europe/')) {
                    if (tzLower.includes('paris')) return 'fr';
                    if (tzLower.includes('berlin')) return 'de';
                    if (tzLower.includes('madrid')) return 'es';
                    if (tzLower.includes('rome')) return 'it';
                    if (tzLower.includes('amsterdam')) return 'nl';
                    if (tzLower.includes('dublin')) return 'ie';
                    if (tzLower.includes('athens')) return 'gr';
                    if (tzLower.includes('vienna')) return 'at';
                    if (tzLower.includes('brussels')) return 'be';
                    if (tzLower.includes('helsinki')) return 'fi';
                    if (tzLower.includes('lisbon')) return 'pt';
                    return 'gb';
                }
                // Middle East / Asia / Pacific
                if (tzLower.startsWith('asia/dubai')) return 'ae';
                if (tzLower.startsWith('australia/')) return 'au';
                if (tzLower.startsWith('asia/singapore')) return 'sg';
                if (tzLower.startsWith('asia/tokyo')) return 'jp';
                if (tzLower.startsWith('asia/bangkok')) return 'th';
            }
        }

        // 2. Language/locale fallback if timezone is neutral/unresolved
        const languages =
            typeof navigator !== 'undefined' && navigator.languages && navigator.languages.length > 0
                ? navigator.languages
                : typeof navigator !== 'undefined' && navigator.language
                  ? [navigator.language]
                  : [];

        for (const lang of languages) {
            if (!lang) continue;

            // Check Intl.Locale
            try {
                if (typeof Intl !== 'undefined' && (Intl as any).Locale) {
                    const loc = new (Intl as any).Locale(lang);
                    if (loc.region) {
                        return loc.region.toLowerCase();
                    }
                }
            } catch {}

            // Regex fallback for tags like "en-IN", "en_IN", "en-US", "fr-FR"
            const match = lang.match(/[-_]([A-Za-z]{2})\b/);
            if (match && match[1]) {
                return match[1].toLowerCase();
            }
        }
    } catch {}

    return 'in';
}

/**
 * Resolves the appropriate currency for the user based on Locale / Country:
 * - India ('in') -> 'INR'
 * - United Kingdom ('gb' / 'uk') -> 'GBP'
 * - Eurozone countries -> 'EUR'
 * - United States ('us') -> 'USD'
 * - Any other country -> defaults to 'USD'
 */
export function detectLocaleCurrency(country?: string): SupportedCurrency {
    const c = (country || detectUserCountry()).toLowerCase();
    if (c === 'in') return 'INR';
    if (c === 'gb' || c === 'uk') return 'GBP';
    if (EUROZONE_COUNTRIES.has(c)) return 'EUR';
    if (c === 'us') return 'USD';
    return 'USD';
}
